import { List } from 'immutable';
import * as THREE from 'three';
import PolyMesh from '../../polyMesh/geometric/model/PolyMesh';
import PolyMap from '../../polyMesh/geometric/model/PolyMap';
import ObjectArrayView from '../../polyMesh/generic/container/ObjectArrayView';
import ObjectsByName from '../../polyMesh/generic/container/ObjectsByName';

const UVMap = {
  schema: {
    mWidth: { type: 'Number', label: 'Width', defaultValue: 1, step: 0.1, minValue: 0.0, animatable: true },
    mHeight: { type: 'Number', label: 'Height', defaultValue: 1, step: 0.1,  minValue: 0.0, animatable: true },
    mLength: { type: 'Number', label: 'Length', defaultValue: 1, step: 0.1,  minValue: 0.0, animatable: true },

    center: { label: 'Center', type: 'Vec3', step: 0.1, defaultValue: new THREE.Vector3(0, 0, 0), animatable: true },
    rotation: { label: 'Rotation', type: 'Vec3', step: 1,  minValue: -360, maxValue: 360, defaultValue: new THREE.Vector3(0, 0, 0), animatable: true },
    uTile: { label: 'U Tile', type: 'Number', step: 0.1, defaultValue: 1.0, animatable: true },
    vTile: { label: 'V Tile', type: 'Number', step: 0.1, defaultValue: 1.0, animatable: true },

    uvProjectionMode: { label: 'Projection', type: 'Options', display: 'Radio',
      values: [], // FIXME UVMAP type // 0, 1, 2, 3
      labels: ['Planar', 'Box', 'Cylindrical', 'Spherical'], defaultValue: 0, /* FIXME PROPER value*/ },
    xAxis: { label: 'Align Local Axis', type: 'Options', display: 'Radio',
      values: [],//0, 1, 2, 3
      labels: ['X', 'Y', 'Z', 'Average Normals'], defaultValue: 1, },
    //uvMapChannel: { label: 'UV Channel', type: 'String', defaultValue: 'default' },

    autoFit: { label: 'Auto Fit', type: 'Button' },
    autoAlign: { label: 'Auto Align', type: 'Button' }, // Hidden until local manipulators fixed
    autoFitAlign: { label: 'Best Fit', type: 'Button' }, // Hidden until local manipulators fixed
    autoCenter: { label: 'Auto Center', type: 'Button' },
    resetUVs: { label: 'Reset', type: 'Button' },

    // SubObjects
    //indices: { label: "Selection", type: 'TextArea', defaultValue: "", display: 'indexStyle' },
    //componentType: { type: 'Object', hidden: true, defaultValue: 'Face' },

    // For backward compatibility
    //scale: { label: 'scale', type: 'Vec3', step: 0.1, defaultValue: {x: 0, y: 0, z: 0}, hidden: true },

    //subdivisions: { label: 'Subdivisions', type: 'Number', defaultValue: 1, minValue: 0, maxValue: 4 },
    //hardEdges: { label: 'Hard Edges', type: 'Boolean', defaultValue: false }
  },

  update: function (operator, primitive) {
    const mesh = primitive.mesh;
    if (!mesh) return primitive;

    const options = {};

    options.uTile = operator.uTile;
    options.vTile = operator.vTile;
    options.uvProjectionMode = operator.uvProjectionMode;
    options.alignmentAxis = operator.xAxis;
    options.center = operator.center;
    options.rotation = operator.rotation;
    options.mapChannel = operator.uvMapChannel;
    options.scale = new THREE.Vector3(operator.mWidth, operator.mHeight, operator.mLength);

    primitive.mesh = applyUVMap(mesh, options);
  },
};

function applyUVMap(polyMesh, uvMapOptions) {

  uvMapOptions = uvMapOptions || {};    // because all values can be defaulted, uvMapOptions should be allowed to be null/undefined too!

  const rotation = uvMapOptions.rotation.multiplyScalar(180 / Math.PI) || new THREE.Vector3();
  const scale = uvMapOptions.scale || new THREE.Vector3(1, 1, 1);
  const center = uvMapOptions.center || new THREE.Vector3();
  const uvProjectionMode = (uvMapOptions.uvProjectionMode !== undefined) ? uvMapOptions.uvProjectionMode : 0;
  const alignmentAxis = (uvMapOptions.alignmentAxis !== undefined) ? uvMapOptions.alignmentAxis : 1;
  const mapChannel = (uvMapOptions.mapChannel !== undefined) ? uvMapOptions.mapChannel : 'default';
  const uTile = (uvMapOptions.uTile !== undefined) ? uvMapOptions.uTile : 1.0;
  const vTile = (uvMapOptions.vTile !== undefined) ? uvMapOptions.vTile : 1.0;

  // Inverse matrix should be used, so uv map follows the gimzo
  const matrixRotation = new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler().setFromVector3(rotation, 'ZYX'));
  const matrixRotationInv = new THREE.Matrix4().getInverse(matrixRotation);

  const faceRangeOffsets = polyMesh.faceRangeOffsets;
  const faceValueIndices = polyMesh.positions.faceValueIndices;
  const values = polyMesh.positions.values;

  const uvFaceValueIndices = new Uint32Array(faceValueIndices.length);
  const uvValues = new ObjectArrayView(THREE.Vector2, faceValueIndices.length);

  const vertex1 = new THREE.Vector3();
  const vertex2 = new THREE.Vector3();
  const faceNormal = new THREE.Vector3();
  const newUV = new THREE.Vector2();

  //for every face
  let faceBegin = 0;
  let faceEnd = 0;
  let uvValueCounter = 0;
  for (let i = 1; i < faceRangeOffsets.length; i++) {
    faceEnd = faceRangeOffsets[i];

    //compute the face normal
    faceNormal.multiplyScalar(0);
    for (let j = faceBegin; j < faceEnd; j++) {

      let k = j + 1;
      if (k >= faceEnd) {
        k = faceBegin;
      }

      values.getAt(faceValueIndices[j], vertex1);
      values.getAt(faceValueIndices[k], vertex2);

      faceNormal.x += (vertex1.y - vertex2.y) * (vertex1.z + vertex2.z);
      faceNormal.y += (vertex1.z - vertex2.z) * (vertex1.x + vertex2.x);
      faceNormal.z += (vertex1.x - vertex2.x) * (vertex1.y + vertex2.y);
    }

    faceNormal.normalize();

    //for every index on that face
    for (let j = faceBegin; j < faceEnd; j++) {

      values.getAt(faceValueIndices[j], vertex1);

      vertex1.sub(center);    // Translate to center
      vertex1.applyMatrix4(matrixRotationInv);   // Apply Inverse rotation to the vertex (to follow gizmo)

      switch (uvProjectionMode) {

      case 0: //Planar
        planarUVMap(vertex1, alignmentAxis, scale, newUV);
      break;

      case 1: //Box
        boxUVMap(vertex1, faceNormal, scale, newUV);
      break;

      case 2: //Cylindrical
        cylindricalUVMap(vertex1, alignmentAxis, scale, newUV);
      break;

      case 3: //Spherical
        sphericalUVMap(vertex1, alignmentAxis, scale, newUV);
      break;

      default:
        newUV.set(0, 0);
      break;
    }

      // Apply tiling
      if (uTile !== 1.0) {
        newUV.x *= uTile;
      }

      if (vTile !== 1.0) {
        newUV.y *= vTile;
      }

      // Avoid disconnected uv faces
      //TODO check for duplicate uv's on that vertex and don't store it twice;

      uvFaceValueIndices[j] = uvValueCounter;
      uvValues.setAt(uvValueCounter++, newUV);
    }

    faceBegin = faceEnd;
  }

  const resultMesh = new PolyMesh(polyMesh);

  const newUVMap = new PolyMap({
    faceRangeOffsets: faceRangeOffsets,
    faceValueIndices: uvFaceValueIndices,
    values: uvValues,
  });

  if (!resultMesh.uvMaps) resultMesh.uvMaps = new ObjectsByName();
  resultMesh.uvMaps.set(mapChannel, newUVMap);

  return resultMesh;
};

function boxUVMap(vertex, normal, scale, uv) {

  let s = 0;
  let t = 0;

  const x = vertex.x;
  const y = vertex.y;
  const z = vertex.z;
  // Face normal
  const nx = Math.abs(normal.x);
  const ny = Math.abs(normal.y);
  const nz = Math.abs(normal.z);

  if (nx >= ny && nx >= nz) {
    s = -z / scale.z + 0.5;
    t = y / scale.y + 0.5;
  }

  if (ny >= nx && ny >= nz) {
    s = x / scale.x + 0.5;
    t = -z / scale.z + 0.5;
  }

  if (nz >= nx && nz >= ny) {
    s = x / scale.x + 0.5;
    t = y / scale.y + 0.5;
  }

  // Otherside of box is mirror of the other side
  // However, only making them negative is not a good solution for tiling, so we add them to 1 to be flipped in positive side
  if (normal.x < 0) {
    s = -s + 1;
  }

  if (normal.y < 0) {
    s = -s + 1;
  }

  if (normal.z < 0) {
    s = -s + 1;
  }

  uv.set(s, t);
  return uv;
};

function planarUVMap(vertex, planeAxis, scale, uv) {

  // Note: In case of plane we will not use scale.z
  let u = ((planeAxis === 0) ? -vertex.z : vertex.x);
  let v = ((planeAxis === 1) ? -vertex.z : vertex.y);

  u = u / scale.x + 0.5;
  v = v / scale.y + 0.5;

  uv.set(u, v);

  return uv;
};

function cylindricalUVMap(vertex, cylinderAxis, scale, uv) {

  let lon;
  let t;

  switch (cylinderAxis) {
    case 0:
      lon = xyz_to_longitude(vertex.z / scale.z, vertex.x / scale.x, -vertex.y / scale.y);
      t = -vertex.x / scale.x + 0.5;
    break;

    case 1:
      lon = xyz_to_longitude(vertex.x / scale.x, vertex.y / scale.y, vertex.z / scale.z);
      t = vertex.y / scale.y + 0.5;
    break;

    case 2:
      lon = xyz_to_longitude(-vertex.x / scale.x, vertex.z / scale.z, -vertex.y / scale.y);
      t = -vertex.z / scale.z + 0.5;
    break;
  }

  // convert it from [0-2PI] to [0-1]
  lon = lon / (Math.PI * 2);
  uv.set(lon, t);

  return uv;
};

function sphericalUVMap(vertex, sphereAxis, scale, uv) {

  let lonlat;
  let lat, lon;

  switch (sphereAxis) {
    case 0:
      lonlat = xyz_to_longitudelatitude(vertex.z / scale.z, vertex.x / scale.x, -vertex.y / scale.y);
    break;

    case 1:
      lonlat = xyz_to_longitudelatitude(vertex.x / scale.x, -vertex.y / scale.y, vertex.z / scale.z);
    break;

    case 2:
      lonlat = xyz_to_longitudelatitude(-vertex.x / scale.x, vertex.z / scale.z, -vertex.y / scale.y);
    break;
  }

  // convert longitude from [0, 2PI] to [0,1], and latitude from [-PI, +PI] to [0,1]
  lon = lonlat.lon / (Math.PI * 2);
  lat = 0.5 - lonlat.lat / Math.PI;
  uv.set(lon, lat);

  return uv;
};


// convert XYZ space to longitude
// Returns longitude is in interval [-PI,+PI]
function xyz_to_longitude(x, y, z) {

  let lon;

  if (x === 0 && z === 0) {

    lon = 0;

  } else {
    lon = Math.atan2(x, z);

    // longitude is in interval [-PI,+PI], so we add two PI to negative longitudes to be between [0,2PI]
    if (lon < 0) {
      lon += (Math.PI * 2);
    }
  }

  return lon;
};

// convert XYZ space to longitude and latitude
// Returns longitude [0, 2PI] and latitude [-PI, +PI]
function xyz_to_longitudelatitude(x, y, z) {

  let lo, la;

  if (x === 0 && z === 0) {
    lo = 0;

    if (y !== 0) {
      la = (y < 0) ? -(Math.PI / 2) : (Math.PI / 2);

    } else {
      la = 0;
    }

  } else {

    lo = Math.atan2(x, z);

    // longitude is in interval [-PI,+PI], so we add two PI to negative longitudes to be between [0,2PI]
    if (lo < 0) {
      lo += Math.PI * 2;
    }

    const h = Math.sqrt(x * x + z * z);
    // latitude is in [-PI,+PI]
    la = Math.atan2(y, h);

  }

  return {
    lon: lo,
    lat: la,
  };
};

export default UVMap;



// WEBPACK FOOTER //
// ./src/operators/PolyMesh/UVMap.js